/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import {
  CELL_TEXT_PRIMARY_CLASS,
  CELL_TEXT_RIGHT_OVERFLOW_CLASS,
  code3Formatter,
  conditionalRowPostCreateDecorator,
  createDefaultGridModel,
  defaultCurrencyCellRendererDecorator,
  falsyAsEmptyCellRenderer,
  falsyCellRenderer,
  isCellValueNegative,
  noneCellRenderer,
  rendererIconByName,
  ROW_DIRTY_CLASS,
  ROW_FOOTER_LIGHT_CLASS,
  ROW_HIDDEN_CLASS,
  textOverlayRowPostCreate,
  truthyCellRenderer,
} from '@gh/common-internal-shared';
import {
  IS_REGION_AU_OR_NZ,
  NumberTypes,
  queryFormEditable,
  State,
  TextTypes,
} from '@gh/common-shared';
import { TranslationService } from '@gh/core-mls';
import {
  buildEditableMultilineTextColumn,
  buildEditableNumberColumn,
  buildNumberColumn,
  cellRendererSelectorForPinnedRow,
  GridModel,
  LayoutBasedGridDefinitionWithModel,
  typedValueFormatter,
} from '@gh/core-ui-grid';
import { isNotNil } from '@gh/core-util';
import { ColDef, GridOptions, ValueFormatterParams } from 'ag-grid-community';
import { isNil } from 'lodash';
import {
  getQuoteVariationId,
  isQuoteVariation,
  isQuoteVariationTotal,
  QuoteVariation,
  quoteVariationFieldState,
  QuoteVariationOrTotal,
  validateQuoteVariationField,
} from '../../../../resources/hlp-quote.model';
import { HlpQuoteDetailsCommandService } from '../hlp-quote-details-command.service';
import { HlpQuoteDetailsState } from '../hlp-quote-details.state';

@Injectable()
export class HlpQuoteDetailsVariationGridDefinitionService extends LayoutBasedGridDefinitionWithModel {

  isAuOrNzRegion = IS_REGION_AU_OR_NZ;
  needRound = false;

  constructor(private translationService: TranslationService,
              private state: State<HlpQuoteDetailsState>,
              private commands: HlpQuoteDetailsCommandService) {
    super();
  }

  buildGridOptions(): GridOptions {

    const result = {
      rowClassRules: {
        [ROW_DIRTY_CLASS]: ({ data }: { data: QuoteVariation }) => !isNil(data.dirtyType),
        [ROW_FOOTER_LIGHT_CLASS]: ({ data }: { data: QuoteVariationOrTotal }) => isQuoteVariationTotal(data),
        [ROW_HIDDEN_CLASS]: ({data}: {data: QuoteVariation}) => data.grayOut,
      },
      processRowPostCreate: conditionalRowPostCreateDecorator(
        ({ node: { data } }) => isQuoteVariationTotal(data),
        textOverlayRowPostCreate(`${this.translationService.get('lbl_changes_total')}:`)),
    };
    return result;
  }

  buildGridModel(): GridModel {
    const result = createDefaultGridModel({
      getId: getQuoteVariationId,
      editable: this.state.select(queryFormEditable),
      validate: validateQuoteVariationField,
      getCellDef: quoteVariationFieldState,
      command: (entity, name, value) => this.commands.setVariationField(getQuoteVariationId(entity), name, value),
    });
    return result;
  }

  hasDecimalPart(value: number): boolean {
    if (this.needRound) {
      return true;
    } else {
      if (value.toString().includes('.')) {
        this.needRound = true;
      }
      return value.toString().includes('.');
    }
  }

  buildColumnDefs(): Hash<ColDef> {
    const sortNumber = (a, b) => {
      if (!a || !b) {
        return 0;
      }
      const aNumber = parseInt(a.split('').replaceAll(',', '').replace('.', '').join(''));
      const bNumber = parseInt(b.split('').replaceAll(',', '').replace('.', '').join(''));
      return aNumber - bNumber;
    };
    const sort = (a, b) => {
      if (!a || !b) {
        return 0;
      }
      const aValue = a.value  || a;
      const bValue = b.value  || b;
      if (aValue < bValue) {
        return -1;
      }
      if (aValue > bValue) {
        return 1;
      }
      return 0;
    };
    const result = {
      'itemCode': {
        comparator: sort,
      },
      'description': {
        ...buildEditableMultilineTextColumn(
          TextTypes.Text,
          this.model.getCellAccessor('description'),
          { minRows: 5, maxRows: 14 }),
        cellRendererSelector: cellRendererSelectorForPinnedRow(noneCellRenderer),
        cellClass: CELL_TEXT_PRIMARY_CLASS,
        comparator: sort,
      },
      'costCenterCode': {
        valueFormatter: code3Formatter,
        cellClass: 'text-right',
        cellRendererSelector: cellRendererSelectorForPinnedRow(noneCellRenderer),
        comparator: sort,
      },
      'itemPrice': {
        ...buildNumberColumn(NumberTypes.Price),
        comparator: sort,
      },
      'origQuantity': {
        ...buildNumberColumn(NumberTypes.Quantity),
        cellRenderer: falsyAsEmptyCellRenderer,
        comparator: sort,
      },
      'quantity': {
        ...buildEditableNumberColumn(NumberTypes.SignedQuantity, this.model.getCellAccessor('quantity')),
        cellRendererSelector: cellRendererSelectorForPinnedRow(noneCellRenderer),
        cellClass: CELL_TEXT_PRIMARY_CLASS,
        comparator: sort,
      },
      'debit': {
        valueGetter: ({ data }: { data: QuoteVariationOrTotal }) =>
          isQuoteVariationTotal(data) ? data.debit : (data.isCredit ? void 0 : data.price),
        ...buildNumberColumn(NumberTypes.Price),
        headerName: this.isAuOrNzRegion
          ? this.translationService.get('lbl_debit_dollar_ex_gst')
          : this.translationService.get('lbl_debit_dollar'),
        cellRendererSelector: cellRendererSelectorForPinnedRow(
          defaultCurrencyCellRendererDecorator(typedValueFormatter(NumberTypes.Price))),
        cellClassRules: {
          [CELL_TEXT_RIGHT_OVERFLOW_CLASS]: ({ data }: { data: QuoteVariationOrTotal }) => isQuoteVariationTotal(data),
        },
        comparator: sort,
      },
      'credit': {
        valueGetter: ({ data }: { data: QuoteVariationOrTotal }) =>
          isQuoteVariationTotal(data) ? data.credit
            : (data.isCredit ? data.price : void 0),
        ...buildNumberColumn(NumberTypes.Price),
        cellRendererSelector: cellRendererSelectorForPinnedRow(
          defaultCurrencyCellRendererDecorator(typedValueFormatter(NumberTypes.Price))),
        headerName: this.isAuOrNzRegion
          ? this.translationService.get('lbl_credit_dollar_ex_gst')
          : this.translationService.get('lbl_credit_dollar'),
        cellClassRules: {
          'warn-color': (params: ValueFormatterParams) => isQuoteVariation(params.data),
        },
        comparator: sort,
      },
      'customerPrice': {
        valueGetter: ({ data }: { data: QuoteVariationOrTotal }) => {
          if (isQuoteVariationTotal(data)) {
            return data.totalPriceWithGst;
          }
          return data.priceWithGst ?
            ((this.hasDecimalPart(data.priceWithGst) || this.needRound) ?
              data.priceWithGst.toFixed(2) : data.priceWithGst)
            : '';
        },
        ...buildNumberColumn(NumberTypes.Price),
        cellRendererSelector: cellRendererSelectorForPinnedRow(
          defaultCurrencyCellRendererDecorator(typedValueFormatter(NumberTypes.Price))),
        cellClassRules: {
          'warn-color': (params: ValueFormatterParams) => params.data && params.data.isCredit
        },        
        comparator: sortNumber,
      },
      'isInPriceBook': {
        cellRenderer: falsyCellRenderer(rendererIconByName('close')),
        cellRendererSelector: cellRendererSelectorForPinnedRow(noneCellRenderer),
        comparator: sort,
      },
      'hasMemo': {
        valueGetter: ({ data }: { data: QuoteVariation }) => isNotNil(data.memo) && data.memo.length > 0,
        cellRenderer: truthyCellRenderer(rendererIconByName('done')),
        comparator: sort,
      },
      'profit': {
        ...buildNumberColumn(NumberTypes.Price),
        cellRendererSelector: cellRendererSelectorForPinnedRow(
          defaultCurrencyCellRendererDecorator(typedValueFormatter(NumberTypes.Price))),
        cellClassRules: {
          'warn-color': (params: ValueFormatterParams) => isQuoteVariation(params.data) && isCellValueNegative(params),
        },
        comparator: sort,
      },
    };
    return result;
  }
}
